"use client"

import CallBlock from "@/app/components/atomic/atoms/CallBlock"
import AISummaryBlockLazy from "@/app/components/atomic/molecules/AISummaryBlockLazy"
import WPContentRenderer from "@/app/components/atomic/molecules/WPContentRenderer"
import EndingPageReviewer from "@/app/components/atomic/molecules/EndingPageReviewer"
import MobileAdvertBlock from "@/app/components/auctor/page/MobileAdvertBlock"
import {useTracking} from "@/app/contexts/tracking-context"
import {Products} from "@/app/enums/Products"
import {IWPContentPayload, TBreadcrumb} from "@/app/models/wp"
import useIntersectionObserver from "@/app/hooks/useIntersectionObserver"
import {setupSidebarIntendedItemsOnScroll} from "@/app/utils/advertizing-block-utils"
import {faqSection, initResponsiveTables} from "@/app/utils/wp-utils"
import type {MarketRate} from "@/app/utils/shortcode-preprocessing"
import type {LemoineSeries} from "@/app/utils/lemoine-counter"
import {PATHS_TO_PRODUCT} from "@/app/utils/context-utils"
import React, {useEffect, useRef} from "react"
import ArgumentsSection from "@/app/components/atomic/organisms/ArgumentsSection"

interface WPPageContentProps {
    showInnerTitle: boolean
    showAISummary: boolean
    title?: string
    product?: string
    pageContent: IWPContentPayload
    children?: any
    preprocessedTools?: Array<{
        id: string
        showFiltersAbove?: boolean
        toolData: any
        template: any
        filters: any[]
        expressionContext?: string
        expressionOnLoad?: string
    }>
    breadcrumb?: TBreadcrumb
    preprocessedComparators?: Array<{
        id: string
        showFiltersAbove?: boolean
        maxResults?: number
        filterListClasses?: string
        gapBetweenFilters?: string
        maxFiltersMobile?: number
        templateConfig: any
        filtersConfig: any
        initialResults: any[]
    }>
    marketRate?: MarketRate | null
    lemoineSeries?: LemoineSeries | null
    globaleNotation?: number
}

const advertBlockObserverOptions: IntersectionObserverInit = {rootMargin: "0px 0px -100% 0px"}

export default function WPPageContent({
                                          pageContent,
                                          showInnerTitle,
                                          showAISummary,
                                          title,
                                          product,
                                          children,
                                          preprocessedTools,
                                          preprocessedComparators,
                                          marketRate,
                                          lemoineSeries,
                                          breadcrumb,
                                          globaleNotation,
                                      }: WPPageContentProps) {
    const isInit = useRef(false)
    const hasSidebar = pageContent.template.hasSidebar
    const sidebarRight = pageContent.template.sidebarRight
    const {track} = useTracking()
    const [showCallBlock, setShowCallBlock] = React.useState(false)
    const [currentUrl, setCurrentUrl] = React.useState<string>("")

    const affiliationPages = [
        "/guide/mutuelle-sante/expatries",
        "/guide/mutuelle-sante/expatries/expatrie-usa",
        "/guide/mutuelle-sante/expatries/canada",
        "/guide/mutuelle-sante/expatries/maroc",
        "/guide/mutuelle-sante/expatries/thailande",
        "/guide/mutuelle-sante/expatries/expatrie-chine",
        "/guide/mutuelle-sante/expatries/cfe",
        "/guide/mutuelle-sante/cpam",
    ]

    useEffect(() => {
        if (typeof window !== "undefined") {
            setCurrentUrl(window.location.href)
            const url = new URL(window.location.href).pathname
            setShowCallBlock(
                [Products.LOAN_INSURANCE, Products.HEALTH, Products.HEALTH_COMPARATOR].includes(product as Products) &&
                !affiliationPages.some((page) => url.startsWith(page))
            )
        }
    }, [product])

    useEffect(() => {
        if (!isInit.current) {
            // Delay event attachment to ensure DOM is fully updated after server-side rendering
            // This allows time for client-side hydration to complete, preventing loss of event listeners
            setTimeout(() => {
                initWPPageScripts(hasSidebar)
            }, 100)
            isInit.current = true
        }
    }, [hasSidebar, pageContent.content])

    // Fire tracking event for strategic page tracking
    useEffect(() => {
        if (typeof window !== "undefined") {
            // Extract first 2 segments from current URL path
            const pathSegments = window.location.pathname.split("/").filter(Boolean)
            const pageSlug = "/" + pathSegments.slice(0, 2).join("/")

            track({
                pageStrategique: pageContent.strategicPage || false,
                pageCocon: pageSlug,
                pageCocons: pageContent.parents,
            })
        }
    }, [pageContent.strategicPage, pageContent.parents])

    const intersectionAnchorRef = useRef<HTMLDivElement>(null)
    const {isIntersecting} = useIntersectionObserver(intersectionAnchorRef, advertBlockObserverOptions)

    return (
        <div
            ref={intersectionAnchorRef}
            className={`flex w-full z-10 ${sidebarRight ? "justify-end" : ""}`}
        >
            <div
                className={`pt-[25px] px-2 md:px-4 overflow-x-clip min-w-0 mx-auto max-w-[960px] md:pl-4
                                ${sidebarRight ? " md:pr-10" : "md:pl-10"}`
                }
            >

                {/* AI Summary Block - placed at the top of the content */}
                {showAISummary && title && currentUrl && <AISummaryBlockLazy title={title} url={currentUrl} />}

                {children}

                {showInnerTitle && title && (
                    <div className={"rm-page-content"}>
                        <h2 dangerouslySetInnerHTML={{__html: title}} />
                    </div>
                )}

                <WPContentRenderer
                    html={pageContent.content}
                    preprocessedTools={preprocessedTools}
                    preprocessedComparators={preprocessedComparators}
                    marketRate={marketRate}
                    lemoineSeries={lemoineSeries}
                />

                <div className="flex flex-col md:flex-row gap-10">
                    {pageContent.reviewer && pageContent.reviewer.verifiedInfoText &&
                        <aside aria-label="Relecture par un expert" className="contents">
                            <EndingPageReviewer
                                reviewer={pageContent.reviewer}
                            />
                        </aside>
                    }

                    {showCallBlock && currentUrl && PATHS_TO_PRODUCT[Products.LOAN_INSURANCE].some((path) => new URL(currentUrl).pathname.startsWith(path)) && <CallBlock />}
                </div>

                {globaleNotation && product === Products.LOAN_INSURANCE && <ArgumentsSection globaleNotation={globaleNotation} />}
            </div>

            {pageContent.defaultAdvertBlock && (
                <MobileAdvertBlock defaultAdvertBlock={pageContent.defaultAdvertBlock} isVisible={isIntersecting} />
            )}
        </div>
    )
}

function initWPPageScripts(hasSidebar: boolean) {
    if (hasSidebar) {
        setupSidebarIntendedItemsOnScroll()
    }
    faqSection()

    // only init responsive tables on md and lower devices
    const isDeviceLowerThanMD = window.matchMedia("(max-width: 992px)").matches
    if (isDeviceLowerThanMD) {
        // TODO: try to render those tables server side
        initResponsiveTables()
    }
}
